import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createSession, fetchFrequentDiners } from "../lib/api";
import "./LandingPage.css";

const currencyOptions = [
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
  "CHF",
  "JPY",
  "KRW",
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [diners, setDiners] = useState<string[]>([]);
  const [frequentDiners, setFrequentDiners] = useState<string[]>([]);
  const [newDiner, setNewDiner] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFrequentDiners()
      .then((items) => setFrequentDiners(items))
      .catch(() => {
        setFrequentDiners([]);
      });
  }, []);

  const trimmedNewDiner = useMemo(() => newDiner.trim(), [newDiner]);

  const addDiner = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    setDiners((prev) =>
      prev.includes(trimmed) ? prev : [...prev, trimmed].slice(0, 30)
    );
    setNewDiner("");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (diners.length === 0) {
      setError("Add at least one diner to start a session.");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await createSession({ diners, currency });
      navigate(`/session/${response.sessionId}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create session");
    } finally {
      setLoading(false);
    }
  };

  const removeDiner = (name: string) => {
    setDiners((prev) => prev.filter((diner) => diner !== name));
  };

  return (
    <div className="landing-root">
      <header className="landing-header">
        <h1>BillSplit</h1>
        <p className="landing-tagline">
          Spin up a shared room, add diners, and keep tabs in real-time.
        </p>
      </header>
      <main className="landing-content">
        <section className="landing-card">
          <form className="landing-form" onSubmit={handleSubmit}>
            <h2>Create a session</h2>
            <label className="landing-label" htmlFor="diner-name">
              Add diner
            </label>
            <div className="landing-input-row">
              <input
                id="diner-name"
                type="text"
                name="diner"
                placeholder="Add name and press enter"
                value={newDiner}
                onChange={(event) => setNewDiner(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addDiner(trimmedNewDiner);
                  }
                }}
                autoComplete="off"
              />
              <button
                type="button"
                className="primary"
                onClick={() => addDiner(trimmedNewDiner)}
                disabled={!trimmedNewDiner}
              >
                Add
              </button>
            </div>
            {frequentDiners.length > 0 && (
              <div className="landing-frequent">
                <span>Frequent diners:</span>
                <div className="chip-row">
                  {frequentDiners.map((name) => (
                    <button
                      key={name}
                      type="button"
                      className="chip"
                      onClick={() => addDiner(name)}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="landing-field">
              <label className="landing-label" htmlFor="currency">
                Currency
              </label>
              <select
                id="currency"
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
              >
                {currencyOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="landing-diners">
              {diners.length === 0 ? (
                <p className="landing-placeholder">
                  Add everyone who is sharing the bill.
                </p>
              ) : (
                <ul>
                  {diners.map((diner) => (
                    <li key={diner}>
                      <span>{diner}</span>
                      <button
                        type="button"
                        className="icon-button"
                        aria-label={`Remove ${diner}`}
                        onClick={() => removeDiner(diner)}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {error && <p className="landing-error">{error}</p>}
            <button
              type="submit"
              className="primary wide"
              disabled={loading || diners.length === 0}
            >
              {loading ? "Creating..." : "Start splitting"}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
};

export default LandingPage;
